import { useState, useEffect, useRef, useCallback } from "react";
import {
  Grid,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplayIcon from "@mui/icons-material/Replay";

import GlassCard from "../General/GlassCard";
import CardHeader from "../General/CardHeader";
import DatagridCustom from "../General/DatagridCustom";
import { useToast } from "../../context/ToastContext";
import {
  uploadJudicialImports,
  getJudicialImports,
  retryJudicialImport,
} from "../../services/JudicialImportService";

const STATUS = {
  pending: { label: "En cola", color: "default" },
  processing: { label: "Procesando", color: "info" },
  done: { label: "Completado", color: "success" },
  failed: { label: "Error", color: "error" },
};

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString("es-AR") : "—");

// Se puede reencolar: los fallidos, y los que quedaron pendientes/procesando
// más de 5 min (colgados porque el worker no los tomó).
const canRetry = (imp) => {
  if (imp.status === "failed") return true;
  if (imp.status === "pending" || imp.status === "processing") {
    const created = new Date(imp.created_at).getTime();
    return Number.isFinite(created) && Date.now() - created > 5 * 60 * 1000;
  }
  return false;
};

const lawyerName = (imp) => {
  if (imp.lawyer) {
    const n = `${imp.lawyer.lastname ?? ""} ${imp.lawyer.name ?? ""}`.trim();
    return n || imp.lawyer.enrollment || `id ${imp.lawyer_id}`;
  }
  return imp.lawyer_id ? `id ${imp.lawyer_id}` : "—";
};

export default function ChargeJudicialImport() {
  const { showToast } = useToast();
  const inputRef = useRef(null);

  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [imports, setImports] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const res = await getJudicialImports({ limit: 100 });
      setImports(res.data?.data ?? []);
    } catch {
      /* silencioso: reintenta en el próximo tick */
    }
  }, []);

  useEffect(() => {
    setLoadingList(true);
    loadList().finally(() => setLoadingList(false));
  }, [loadList]);

  useEffect(() => {
    const running = imports.some(
      (i) => i.status === "pending" || i.status === "processing"
    );
    if (!running) return undefined;
    const t = setInterval(loadList, 4000);
    return () => clearInterval(t);
  }, [imports, loadList]);

  const pickFiles = (fileList) => {
    const arr = Array.from(fileList || []).filter((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".json") || n.endsWith(".zip");
    });
    if (arr.length === 0) {
      showToast("Elegí archivos .json del outbox, o un .zip que los contenga.", "info");
      return;
    }
    setSelected((prev) => {
      const names = new Set(prev.map((f) => f.name));
      return [...prev, ...arr.filter((f) => !names.has(f.name))];
    });
  };

  const removeSelected = (name) =>
    setSelected((prev) => prev.filter((f) => f.name !== name));

  const handleUpload = async () => {
    if (selected.length === 0) return;
    const fd = new FormData();
    selected.forEach((f) => fd.append("files[]", f));

    setUploading(true);
    try {
      const res = await uploadJudicialImports(fd);
      showToast(res.data?.message || "Archivos recibidos.", "success");
      setSelected([]);
      if (inputRef.current) inputRef.current.value = "";
      await loadList();
    } catch (error) {
      showToast(
        error.response?.data?.message || "No se pudo subir. Revisá los archivos.",
        "error"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRetry = async (id) => {
    try {
      await retryJudicialImport(id);
      showToast("Reencolado.", "success");
      loadList();
    } catch (error) {
      showToast(error.response?.data?.message || "No se pudo reencolar.", "error");
    }
  };

  const totalSize = selected.reduce((a, f) => a + f.size, 0);

  const columns = [
    {
      field: "original_name",
      headerName: "Archivo",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
          <Box sx={{ fontWeight: 600, wordBreak: "break-all" }}>
            {params.row.original_name}
          </Box>
          <Box sx={{ fontSize: 12, opacity: 0.6 }}>
            {fmtBytes(params.row.size_bytes)}
            {params.row.part
              ? ` · parte ${params.row.part}${params.row.parts ? `/${params.row.parts}` : ""}`
              : ""}
          </Box>
        </Box>
      ),
    },
    {
      field: "lawyer",
      headerName: "Abogado",
      flex: 1.3,
      minWidth: 130,
      sortable: false,
      valueGetter: (_v, row) => lawyerName(row),
    },
    {
      field: "status",
      headerName: "Estado",
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => {
        const st = STATUS[params.row.status] || STATUS.pending;
        return <Chip label={st.label} color={st.color} size="small" />;
      },
    },
    {
      field: "expedientes",
      headerName: "Exped.",
      description: "Expedientes procesados / total del archivo",
      flex: 0.7,
      minWidth: 90,
      align: "right",
      headerAlign: "right",
      sortable: false,
      valueGetter: (_v, row) =>
        row.expedientes_total
          ? `${row.expedientes_done} / ${row.expedientes_total}`
          : `${row.expedientes_done}`,
    },
    {
      field: "operations_new",
      headerName: "Nuevas",
      description: "Operaciones nuevas importadas",
      flex: 0.6,
      minWidth: 85,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "skipped_count",
      headerName: "Omit.",
      description: "Expedientes del archivo que no están registrados en jex (no se crean)",
      flex: 0.5,
      minWidth: 75,
      align: "right",
      headerAlign: "right",
      valueGetter: (v) => v ?? 0,
    },
    {
      field: "errors_count",
      headerName: "Errores",
      flex: 0.5,
      minWidth: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "finished_at",
      headerName: "Fin",
      flex: 1,
      minWidth: 140,
      valueGetter: (v) => fmtDate(v),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        canRetry(params.row) ? (
          <Tooltip
            title={
              params.row.status === "failed"
                ? "Reintentar"
                : "Reencolar (parece colgado)"
            }
          >
            <IconButton size="small" onClick={() => handleRetry(params.row.id)}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mt: 2, px: { xs: 0, md: 1 } }}>
      {/* Columna izquierda: subida */}
      <Grid size={{ xs: 12, md: 5, lg: 4 }}>
        <GlassCard sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
          <CardHeader
            title="Importar novedades"
            description="Subí los .json del outbox del importador local (o un .zip con todos). Se procesan en segundo plano y actualizan los expedientes."
            icon={<UploadFileIcon />}
          />

          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickFiles(e.dataTransfer.files);
            }}
            onClick={() => inputRef.current?.click()}
            sx={{
              mt: 2,
              p: 2.5,
              border: "1.5px dashed",
              borderColor: "divider",
              borderRadius: 2,
              textAlign: "center",
              cursor: "pointer",
              transition: "border-color .15s, background .15s",
              "&:hover": { borderColor: "primary.main" },
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".json,.zip,application/json,application/zip"
              multiple
              hidden
              onChange={(e) => pickFiles(e.target.files)}
            />
            <UploadFileIcon color="action" />
            <Box sx={{ mt: 0.5, fontWeight: 600 }}>
              Arrastrá los archivos o hacé clic
            </Box>
            <Box sx={{ fontSize: 13, opacity: 0.6 }}>
              .json del outbox, o un .zip (del .zip se leen solo los .json)
            </Box>
          </Box>

          {selected.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ fontSize: 13, opacity: 0.7, mb: 1 }}>
                {selected.length} archivo(s) · {fmtBytes(totalSize)}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selected.map((f) => (
                  <Chip
                    key={f.name}
                    label={`${f.name} (${fmtBytes(f.size)})`}
                    onDelete={() => removeSelected(f.name)}
                    deleteIcon={<DeleteOutlineIcon />}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
              <Button
                variant="contained"
                startIcon={
                  uploading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <UploadFileIcon />
                  )
                }
                disabled={uploading}
                onClick={handleUpload}
                sx={{ mt: 2 }}
              >
                {uploading ? "Subiendo…" : "Subir e importar"}
              </Button>
            </Box>
          )}
        </GlassCard>
      </Grid>

      {/* Columna derecha: listado de importaciones */}
      <Grid size={{ xs: 12, md: 7, lg: 8 }}>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <DatagridCustom
            title="Importaciones recientes"
            icon={<UploadFileIcon sx={{ fontSize: 28 }} />}
            headerActions={
              <Tooltip title="Actualizar">
                <IconButton onClick={loadList} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            }
            rows={imports}
            columns={columns}
            loading={loadingList}
            getRowId={(row) => row.id}
            getRowHeight={() => 60}
            hideExport
          />
        </Box>
      </Grid>
    </Grid>
  );
}
